import React from "react";
import { useTranslation } from "react-i18next";
import { TreatmentPlanAppointment } from "../../../../services/treatmentPlanService";
import {
  AppointmentContainer,
  AppointmentHeader,
  AppointmentTitle,
  AppointmentBadge,
  AppointmentMeta,
  MetaItem,
  MetaLabel,
  MetaValue,
  AppointmentDescription,
  ProcedureCard,
  ProcedureLayout,
  ProcedureContentColumn,
  ProcedureDetailsColumn,
  ProcedureTitle,
  ProcedureCdtExplanation,
  ProcedureStepDescription,
  ProcedureDetailItemCdt,
  ProcedureDetailItemFee,
  ProcedureDetailLabel,
  ProcedureDetailValue,
  ProcedureDetailHint,
} from "./TreatmentAppointment.styled";

interface TreatmentAppointmentProps {
  appointment: TreatmentPlanAppointment;
  appointmentNumber: number;
}

const TreatmentAppointment: React.FC<TreatmentAppointmentProps> = ({
  appointment,
  appointmentNumber,
}) => {
  const { t } = useTranslation();

  // Get total fee for the appointment - use totalFee from API if available
  const getAppointmentFee = () => {
    if (appointment.totalFee != null) {
      return appointment.totalFee;
    }
    // Fallback: calculate from steps
    let total = 0;
    appointment.steps.forEach((step) => {
      if (step.fee != null) {
        total += step.fee;
      } else {
        step.codeSuggestions.forEach((code) => {
          total += code.defaultFee || 0;
        });
      }
    });
    return total;
  };

  // Get total duration for the appointment - use totalDurationMinutes from API if available
  const getAppointmentDuration = () => {
    if (appointment.totalDurationMinutes != null) {
      return `${appointment.totalDurationMinutes} min`;
    }
    // Fallback: calculate from steps
    let totalMinutes = 0;
    appointment.steps.forEach((step) => {
      totalMinutes += step.durationMinutes ?? 20;
    });
    return `${totalMinutes} min`;
  };

  // Get procedure fee - use step.fee if available, otherwise calculate from code suggestions
  const getProcedureFee = (step: TreatmentPlanAppointment["steps"][0]) => {
    if (step.fee != null) {
      return step.fee;
    }
    return step.codeSuggestions.reduce(
      (sum, code) => sum + (code.defaultFee || 0),
      0,
    );
  };

  // Get procedure explanation from CDT code descriptions
  const getProcedureExplanation = (
    step: TreatmentPlanAppointment["steps"][0],
  ) => {
    const descriptions = step.codeSuggestions
      .map((code) => code.description)
      .filter(Boolean);
    return descriptions.join("\n");
  };

  // Get CDT codes formatted - 2 per row
  const formatCdtCodes = (
    codeSuggestions: TreatmentPlanAppointment["steps"][0]["codeSuggestions"],
  ) => {
    const codes = codeSuggestions.map((code) => code.code);
    if (codes.length <= 2) {
      return codes.join(", ");
    }
    // Group codes in pairs
    const pairs: string[] = [];
    for (let i = 0; i < codes.length; i += 2) {
      if (i + 1 < codes.length) {
        pairs.push(`${codes[i]}, ${codes[i + 1]}`);
      } else {
        pairs.push(codes[i]);
      }
    }
    return pairs.join("\n");
  };

  // Get option label from conditionalPath (e.g., "A" -> "Option A", "Option 1" -> "1")
  const getOptionLabel = () => {
    if (
      !appointment.conditionalPath ||
      appointment.conditionalPath.trim() === ""
    ) {
      return null;
    }
    const rawPath = appointment.conditionalPath.trim();
    // If conditionalPath already starts with "Option", extract just the label part
    // Otherwise, return as-is (translation will add "Option" prefix)
    return rawPath.toLowerCase().startsWith("option")
      ? rawPath.replace(/^option\s*/i, "")
      : rawPath;
  };

  const optionLabel = getOptionLabel();
  const totalFee = getAppointmentFee();
  const duration = getAppointmentDuration();

  return (
    <AppointmentContainer>
      <AppointmentHeader>
        <AppointmentTitle>
          {t("treatmentPlan.appointment", { number: appointmentNumber })}
          {optionLabel && (
            <AppointmentBadge>
              {t("treatmentPlan.optionLabel", { label: optionLabel })}
            </AppointmentBadge>
          )}
        </AppointmentTitle>
        <AppointmentMeta>
          <MetaItem>
            <MetaLabel>{t("treatmentPlan.fee")}</MetaLabel>
            <MetaValue>${totalFee.toLocaleString("en-US")}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{t("treatmentPlan.duration")}</MetaLabel>
            <MetaValue>{duration}</MetaValue>
          </MetaItem>
        </AppointmentMeta>
      </AppointmentHeader>

      {appointment.notes && (
        <AppointmentDescription>{appointment.notes}</AppointmentDescription>
      )}

      {appointment.steps
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((step) => {
          const explanation = getProcedureExplanation(step);
          const fee = getProcedureFee(step);
          return (
            <ProcedureCard key={step.id}>
              <ProcedureLayout>
                <ProcedureContentColumn>
                  <ProcedureTitle>{step.name}</ProcedureTitle>
                  {step.description && (
                    <ProcedureCdtExplanation>
                      {step.description}
                    </ProcedureCdtExplanation>
                  )}
                  {explanation && (
                    <ProcedureStepDescription>
                      {explanation}
                    </ProcedureStepDescription>
                  )}
                </ProcedureContentColumn>
                <ProcedureDetailsColumn>
                  <ProcedureDetailItemCdt>
                    <ProcedureDetailLabel>
                      {t("treatmentPlan.cdtCode")}
                    </ProcedureDetailLabel>
                    <ProcedureDetailValue
                      sx={{ wordBreak: "break-word", whiteSpace: "pre-line" }}
                    >
                      {formatCdtCodes(step.codeSuggestions) || "-"}
                    </ProcedureDetailValue>
                  </ProcedureDetailItemCdt>
                  <ProcedureDetailItemFee>
                    <ProcedureDetailLabel>
                      {t("treatmentPlan.fee")}
                    </ProcedureDetailLabel>
                    <ProcedureDetailValue sx={{ wordBreak: "break-word" }}>
                      ${fee.toLocaleString("en-US")}{" "}
                      <ProcedureDetailHint>
                        {t("treatmentPlan.basedOnClinicRates")}
                      </ProcedureDetailHint>
                    </ProcedureDetailValue>
                  </ProcedureDetailItemFee>
                </ProcedureDetailsColumn>
              </ProcedureLayout>
            </ProcedureCard>
          );
        })}
    </AppointmentContainer>
  );
};

export default TreatmentAppointment;
